import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useGetRemainingCredits from 'components/Dashboard/hooks/useGetRemainingCredits';
import { searchIncludesTitleConditional } from 'components/Dashboard/StakeHolders/searchIncludesTitle';
import getUrlParam, { decodeUrlQueryValue, getUrlParamRaw } from 'components/Dashboard/utilities/getUrlParam';
import { store } from 'components/Store';
import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const useStakeholderAISummaryTab = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists } = state;

  const { StakeholderInformation } = props ?? {};
  const { officalName, categories } = StakeholderInformation;

  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [currentlyProcessing, setCurrentlyProcessing] = useState({ attempt: 0, state: false });
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [summaryFinishes, setSummaryFinishes] = useState(false);

  const { generalApiCall } = useGeneralApiCall();
  const { updateRemainingCredits } = useGetRemainingCredits();

  const params = useParams();
  const { id } = params;
  const topicParameter = getUrlParam('topic-id');
  const searchParameter = getUrlParamRaw('search') ? decodeUrlQueryValue(getUrlParamRaw('search')) : '';
  const hasSearchQuery = !!searchParameter?.trim();
  const isAllTopics = topicParameter === 'all';
  const topic = keywordsLists?.find((item) =>
    topicParameter === 'all' ? item?.id === null : item?.id === parseInt(topicParameter)
  );

  const specialAdviser = categories?.includes('Special Adviser');
  const searchCondition =
    (!!topicParameter || hasSearchQuery) && !searchIncludesTitleConditional(officalName) && !specialAdviser;

  const initialCall = useRef(null);
  initialCall.current = async ({ source, secondCall }) => {
    if (searchCondition) {
      try {
        if (!secondCall) {
          setIsLoading(true);
        }
        const queryParams = new URLSearchParams();
        if (hasSearchQuery) {
          queryParams.set('searchQuery', searchParameter);
        } else if (!isAllTopics && topic?.id !== undefined) {
          queryParams.set('keywordListId', topic?.id);
        }
        queryParams.set('contactId', id);
        const results = await generalApiCall({
          pathname: `/api/stakeholder/get-summary?${queryParams.toString()}`,
          method: 'get',
          needsAuthentication: true,
          requestSource: source,
          notShowErrorMessage: true,
          returnError: true,
          returnCompleteRequest: true,
        });
        if (!!results) {
          if (results?.status !== 404 && results?.status !== 400) {
            setContent(results?.data);
            setCurrentlyProcessing({ attempt: currentlyProcessing.attempt + 1, state: !results?.data?.isReady });
            if (secondCall & results?.data?.isReady) {
              setSummaryFinishes(true);
              setTimeout(() => {
                setSummaryFinishes(false);
              }, 5000);
            }
          } else {
            setCurrentlyProcessing({ attempt: 0, state: false });
            setContent(null);
          }
          setIsLoading(false);
        }
      } catch (error) {}
    } else {
      setCurrentlyProcessing({ attempt: 0, state: false });
    }
  };

  useEffect(() => {
    const source = new AbortController();

    let timeoutId;
    const callAgainResults = () => {
      timeoutId = setTimeout(async () => {
        try {
          await initialCall.current({ source, secondCall: true });
        } catch (error) {
          console.error(error);
        }
      }, 5000);
    };
    const { state } = currentlyProcessing;
    if (state) {
      callAgainResults();
    }
    return () => {
      source.abort('New call for AI summary canceled by the user.');
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [currentlyProcessing, topicParameter, searchParameter]);

  useEffect(() => {
    const source = new AbortController();
    initialCall.current({ source });
    return () => {
      source.abort('Stakeholder AI summary loading cancelled by the user');
    };
  }, [topicParameter, searchParameter]);

  const generateSummary = async () => {
    try {
      setCurrentlyProcessing({ attempt: 0, state: false });
      setGeneratingSummary(true);
      const pathname = '/api/stakeholder/generate-summary';
      const requestProperties = {
        keywordListId: null,
        stakeholderContactId: parseInt(id),
        overwrite: true,
      };
      if (hasSearchQuery) {
        requestProperties.searchQuery = searchParameter;
      } else if (!isAllTopics && topic?.id !== undefined) {
        requestProperties.keywordListId = topic?.id;
      }
      const results = await generalApiCall({
        pathname,
        method: 'post',
        requestProperties,
        needsAuthentication: true,
        notShowErrorMessage: true,
      });
      if (!!results) {
        if (content === null) {
          await updateRemainingCredits();
        }
        setCurrentlyProcessing({ attempt: currentlyProcessing.attempt + 1, state: !results?.isReady });
        setContent(results);
      }
    } catch (error) {
    } finally {
      setGeneratingSummary(false);
    }
  };
  return {
    generateSummary,
    generatingSummary,
    isLoadingStakeholderAISummary: isLoading,
    content,
    currentlyProcessing,
    summaryFinishes,
  };
};

export default useStakeholderAISummaryTab;
